import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, Connection, AnalyticsSnapshot, PostEngagementStat, EngagementHourSlot, TopEngagementSlot, EngagerOverlapResult } from '../api/client'
import { useWorkspaceContext } from '../hooks/useWorkspaceContext'

interface LiveInsight {
  connectionId: number
  platformName: string
  displayName: string
  followerCount: number
  postCount: number
  engagementRate: number
  reachTotal: number
  impressionsTotal: number
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const HOURS = Array.from({ length: 24 }, (_, i) => i)

function formatHour(h: number) {
  if (h === 0) return '12am'
  if (h < 12) return `${h}am`
  if (h === 12) return '12pm'
  return `${h - 12}pm`
}

export default function Analytics() {
  const { activeWorkspace } = useWorkspaceContext()
  const wsId = activeWorkspace?.workspaceID ?? 0
  const navigate = useNavigate()
  const [snapshots, setSnapshots] = useState<AnalyticsSnapshot[]>([])
  const [allConnections, setAllConnections] = useState<Connection[]>([])
  const [, setPostStats] = useState<PostEngagementStat[]>([])
  const [heatmap, setHeatmap] = useState<EngagementHourSlot[]>([])
  const [topSlots, setTopSlots] = useState<TopEngagementSlot[]>([])
  const [overlap, setOverlap] = useState<EngagerOverlapResult | null>(null)
  const [days, setDays] = useState(30)
  const [utmUrl, setUtmUrl] = useState('')
  const [utmSource, setUtmSource] = useState('')
  const [utmMedium, setUtmMedium] = useState('')
  const [utmCampaign, setUtmCampaign] = useState('')
  const [builtUtm, setBuiltUtm] = useState('')
  const [liveInsights, setLiveInsights] = useState<LiveInsight[]>([])
  const [insightsLoading, setInsightsLoading] = useState(false)
  const [reauthConnections, setReauthConnections] = useState<{ connectionID: number; platformName: string; displayName: string; lastTokenError?: string }[]>([])
  const [showReauthModal, setShowReauthModal] = useState(false)

  const fetchLiveInsights = useCallback(async () => {
    if (allConnections.length === 0) return
    setInsightsLoading(true)
    const results: LiveInsight[] = []
    for (const conn of allConnections) {
      try {
        const data = await api.post<{ followerCount: number; postCount: number; engagementRate: number; reachTotal: number; impressionsTotal: number }>(
          `/workspaces/${wsId}/analytics/fetch-insights/${conn.connectionID}`)
        results.push({ connectionId: conn.connectionID, platformName: conn.platformName || '', displayName: conn.displayName || '', ...data })
      } catch (err) {
        console.error(`[SMM] insights fetch failed for ${conn.displayName}`, err)
      }
    }
    setLiveInsights(results)
    setInsightsLoading(false)
  }, [allConnections, wsId])

  useEffect(() => {
    Promise.all([
      api.get<AnalyticsSnapshot[]>(`/workspaces/${wsId}/analytics/snapshots?days=${days}`),
      api.get<PostEngagementStat[]>(`/workspaces/${wsId}/analytics/post-engagement?days=${days}`),
      api.get<EngagementHourSlot[]>(`/workspaces/${wsId}/analytics/heatmap`),
      api.get<TopEngagementSlot[]>(`/workspaces/${wsId}/analytics/top-slots?topN=5`),
      api.get<Connection[]>(`/workspaces/${wsId}/connections`).catch(() => []),
    ]).then(([s, p, h, t, c]) => {
      setSnapshots(s); setPostStats(p); setHeatmap(h); setTopSlots(t); setAllConnections(c)
    }).catch(err => console.error("[SMM]", err))

    api.get<{ connectionID: number; platformName: string; displayName: string; needsReauth: boolean; lastTokenError?: string }[]>(
      `/workspaces/${wsId}/inbox/connection-health`
    ).then(items => {
      const needs = items.filter(i => i.needsReauth)
      if (needs.length > 0) {
        setReauthConnections(needs)
        setShowReauthModal(true)
      }
    }).catch(() => {})
  }, [days])

  useEffect(() => {
    if (topSlots.length >= 2) {
      const a = topSlots[0], b = topSlots[1]
      api.get<EngagerOverlapResult>(
        `/workspaces/${wsId}/analytics/engager-overlap?slotADay=${a.dayOfWeek}&slotAHour=${a.hourOfDay}&slotBDay=${b.dayOfWeek}&slotBHour=${b.hourOfDay}`
      ).then(setOverlap).catch(err => console.error("[SMM]", err))
    }
  }, [topSlots])

  const maxEngagement = useMemo(() =>
    Math.max(1, ...heatmap.map(h => h.totalEngagement)), [heatmap])

  const getSlotValue = (day: number, hour: number) => {
    const slots = heatmap.filter(h => h.dayOfWeek === day && h.hourOfDay === hour)
    return slots.reduce((sum, s) => sum + s.totalEngagement, 0)
  }

  const handleBuildUtm = () => {
    if (!utmUrl || !utmSource) return
    const params = new URLSearchParams()
    params.set('utm_source', utmSource)
    if (utmMedium) params.set('utm_medium', utmMedium)
    if (utmCampaign) params.set('utm_campaign', utmCampaign)
    const sep = utmUrl.includes('?') ? '&' : '?'
    setBuiltUtm(`${utmUrl}${sep}${params}`)
  }

  return (
    <div className="smm-analytics">
      {showReauthModal && reauthConnections.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.45)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowReauthModal(false)}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '24px 28px', maxWidth: 480, width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#d32f2f' }}>
              Reconnect Required
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.88rem', color: '#555', lineHeight: 1.5 }}>
              The following connections have expired or encountered an error. Analytics data may be unavailable until reconnected.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {reauthConnections.map(c => (
                <div key={c.connectionID} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', borderRadius: 8, background: '#fff3f3', border: '1px solid #ffcdd2'
                }}>
                  <div>
                    <strong style={{ fontSize: '0.88rem' }}>{c.platformName}</strong>
                    <span style={{ fontSize: '0.8rem', color: '#777', marginLeft: 6 }}>{c.displayName}</span>
                    {c.lastTokenError && (
                      <div style={{ fontSize: '0.75rem', color: '#b71c1c', marginTop: 2 }}>{c.lastTokenError}</div>
                    )}
                  </div>
                  <button
                    onClick={() => { setShowReauthModal(false); navigate(`/connections?reconnect=${encodeURIComponent(c.platformName)}`) }}
                    style={{
                      padding: '5px 14px', borderRadius: 6, border: 'none', fontSize: '0.82rem',
                      background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap'
                    }}>Reconnect</button>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'right' }}>
              <button onClick={() => setShowReauthModal(false)} style={{
                padding: '6px 18px', borderRadius: 6, border: '1px solid #ccc',
                background: '#f5f5f5', cursor: 'pointer', fontSize: '0.82rem'
              }}>Dismiss</button>
            </div>
          </div>
        </div>
      )}
      <div className="smm-analytics-header">
        <h1>Analytics</h1>
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="smm-select">
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
      </div>

      {allConnections.length > 0 && (
        <div className="smm-card">
          <h3>Connected Accounts ({allConnections.length})</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            {allConnections.map(c => {
              const hasData = snapshots.some(s => s.connectionID === c.connectionID)
              return (
                <div key={c.connectionID} style={{
                  padding: '0.4rem 0.75rem', borderRadius: 6,
                  border: `1px solid ${hasData ? '#2ec27e' : '#e5a50a'}`,
                  background: hasData ? 'rgba(46,194,126,0.06)' : 'rgba(229,165,10,0.06)',
                  fontSize: '0.85rem',
                }}>
                  <span className="smm-platform-badge" style={{ marginRight: '0.35rem' }}>{c.platformName}</span>
                  <strong>{c.displayName}</strong>
                  {!hasData && <span className="smm-hint" style={{ marginLeft: '0.35rem' }}>(no snapshots yet)</span>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {(() => {
        const hasLive = liveInsights.length > 0
        if (!hasLive && snapshots.length === 0) return null
        const rows = hasLive
          ? liveInsights.map(li => ({
              key: li.connectionId,
              platformName: li.platformName,
              displayName: li.displayName,
              followerCount: li.followerCount,
              postCount: li.postCount,
              reachTotal: li.reachTotal,
              impressionsTotal: li.impressionsTotal,
              engagementRate: li.engagementRate,
            }))
          : Object.values(
              snapshots.reduce<Record<number, typeof snapshots[0]>>((acc, s) => {
                if (!acc[s.connectionID] || s.snapshotDate > acc[s.connectionID].snapshotDate)
                  acc[s.connectionID] = s
                return acc
              }, {})
            ).map(s => ({
              key: s.snapshotID,
              platformName: s.platformName,
              displayName: s.displayName,
              followerCount: s.followerCount,
              postCount: s.postCount,
              reachTotal: s.reachTotal,
              impressionsTotal: s.impressionsTotal,
              engagementRate: s.engagementRate,
            }))
        return (
          <div className="smm-card" style={insightsLoading ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Platform Snapshots</h3>
              <span style={{ fontSize: '0.8rem', color: hasLive ? '#27ae60' : '#e67e22' }}>
                {hasLive ? '(live)' : '(cached)'}
              </span>
              {insightsLoading && <span style={{ fontSize: '0.8rem', color: '#3498db' }}>Refreshing...</span>}
            </div>
            <table className="smm-table">
              <thead>
                <tr><th>Platform</th><th>Account</th><th>Followers</th><th>Posts</th><th>Reach</th><th>Impressions</th><th>Eng. Rate</th></tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.key}>
                    <td>{r.platformName}</td>
                    <td>{r.displayName}</td>
                    <td>{r.followerCount.toLocaleString()}</td>
                    <td>{r.postCount}</td>
                    <td>{r.reachTotal.toLocaleString()}</td>
                    <td>{r.impressionsTotal.toLocaleString()}</td>
                    <td>{r.engagementRate != null ? `${(r.engagementRate * 100).toFixed(2)}%` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })()}

      <PostEngagementGrid wsId={wsId} />
      <TikTokVideoPerformance wsId={wsId} connections={allConnections} />

      <div className="smm-card">
        <h3>Best Time to Post</h3>
        {heatmap.length === 0 ? (
          <p className="smm-empty">Not enough data yet. Post for 30+ days to see patterns.</p>
        ) : (
          <>
            <div className="smm-heatmap">
              <div className="smm-heatmap-corner"></div>
              {HOURS.map(h => <div key={h} className="smm-heatmap-hour">{formatHour(h)}</div>)}
              {DAYS_OF_WEEK.map((dayName, dayIdx) => (
                <div key={dayIdx} className="smm-heatmap-row">
                  <div className="smm-heatmap-day">{dayName}</div>
                  {HOURS.map(h => {
                    const val = getSlotValue(dayIdx, h)
                    const intensity = val / maxEngagement
                    return (
                      <div key={h} className="smm-heatmap-cell"
                        style={{ background: `rgba(67, 97, 238, ${intensity})` }}
                        title={`${dayName} ${formatHour(h)}: ${val} engagement`}>
                        {val > 0 ? val : ''}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>

            {topSlots.length > 0 && (
              <div className="smm-top-slots">
                <h4>Top Engagement Windows</h4>
                {topSlots.map((s, i) => (
                  <div key={i} className="smm-top-slot">
                    <strong>{DAYS_OF_WEEK[s.dayOfWeek]} {formatHour(s.hourOfDay)}-{formatHour(s.hourOfDay + 1)}</strong>
                    <span>{s.engagementSharePct.toFixed(1)}% of total engagement</span>
                    <span className="smm-hint">({s.slotPostCount} posts, {s.slotEngagement} interactions)</span>
                  </div>
                ))}
              </div>
            )}

            {overlap && topSlots.length >= 2 && (
              <div className="smm-overlap-card">
                <h4>Engager Overlap: {DAYS_OF_WEEK[topSlots[0].dayOfWeek]} {formatHour(topSlots[0].hourOfDay)} vs {DAYS_OF_WEEK[topSlots[1].dayOfWeek]} {formatHour(topSlots[1].hourOfDay)}</h4>
                <p>
                  {overlap.overlapPctOfA.toFixed(0)}% of slot 1 engagers also engaged in slot 2.
                  {overlap.overlapPctOfA > 50
                    ? ' High overlap -- these are your super-fans. One post reaches them either way.'
                    : ' Low overlap -- largely unique audiences. Worth posting in both windows.'}
                </p>
                <span className="smm-hint">{overlap.slotAEngagers} vs {overlap.slotBEngagers} unique engagers, {overlap.overlapCount} shared</span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="smm-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Live Platform Insights</h3>
          <button onClick={fetchLiveInsights} className="smm-btn smm-btn-primary smm-btn-sm" disabled={insightsLoading}>
            {insightsLoading ? 'Fetching...' : 'Fetch Insights'}
          </button>
        </div>
        {liveInsights.length === 0 && !insightsLoading && (
          <p className="smm-empty">Click "Fetch Insights" to pull live data from connected platforms.</p>
        )}
        {liveInsights.length > 0 && (
          <table className="smm-table">
            <thead>
              <tr><th>Platform</th><th>Account</th><th>Followers</th><th>Posts</th><th>Reach</th><th>Impressions</th><th>Eng. Rate</th></tr>
            </thead>
            <tbody>
              {liveInsights.map(i => (
                <tr key={i.connectionId}>
                  <td>{i.platformName}</td>
                  <td>{i.displayName}</td>
                  <td>{i.followerCount.toLocaleString()}</td>
                  <td>{i.postCount}</td>
                  <td>{i.reachTotal.toLocaleString()}</td>
                  <td>{i.impressionsTotal.toLocaleString()}</td>
                  <td>{(i.engagementRate * 100).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="smm-card">
        <h3>UTM Link Builder</h3>
        <div className="smm-utm-form">
          <input className="smm-input" value={utmUrl} onChange={e => setUtmUrl(e.target.value)} placeholder="Destination URL" />
          <input className="smm-input" value={utmSource} onChange={e => setUtmSource(e.target.value)} placeholder="Source (required)" />
          <input className="smm-input" value={utmMedium} onChange={e => setUtmMedium(e.target.value)} placeholder="Medium" />
          <input className="smm-input" value={utmCampaign} onChange={e => setUtmCampaign(e.target.value)} placeholder="Campaign" />
          <button onClick={handleBuildUtm} className="smm-btn smm-btn-primary">Build</button>
        </div>
        {builtUtm && (
          <div className="smm-utm-result">
            <code>{builtUtm}</code>
            <button onClick={() => navigator.clipboard.writeText(builtUtm)} className="smm-btn smm-btn-sm">Copy</button>
          </div>
        )}
      </div>
    </div>
  )
}

interface TikTokVideo {
  id: string
  title: string
  createTime: number
  likeCount: number
  commentCount: number
  shareCount: number
  viewCount: number
  thumbnailUrl: string
}

function TikTokVideoPerformance({ wsId, connections }: { wsId: number; connections: Connection[] }) {
  const [videos, setVideos] = useState<TikTokVideo[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedConn, setSelectedConn] = useState<number | null>(null)

  const tiktokConns = connections.filter(c =>
    c.platformName === 'TikTok' || c.platformName === 'TikTok Business')

  useEffect(() => {
    if (tiktokConns.length > 0 && selectedConn === null)
      setSelectedConn(tiktokConns[0].connectionID)
  }, [tiktokConns.length])

  const fetchVideos = useCallback(async () => {
    if (!selectedConn) return
    setLoading(true)
    setError('')
    try {
      const data = await api.get<TikTokVideo[]>(
        `/workspaces/${wsId}/connections/${selectedConn}/tiktok-videos`)
      setVideos(data)
    } catch (err: any) {
      console.error('[SMM] TikTok video list fetch failed', err)
      setError(err?.message || 'Failed to load TikTok videos')
    }
    setLoading(false)
  }, [wsId, selectedConn])

  useEffect(() => { if (selectedConn) fetchVideos() }, [fetchVideos])

  if (tiktokConns.length === 0) return null

  const totalViews = videos.reduce((s, v) => s + v.viewCount, 0)
  const totalLikes = videos.reduce((s, v) => s + v.likeCount, 0)
  const totalComments = videos.reduce((s, v) => s + v.commentCount, 0)
  const totalShares = videos.reduce((s, v) => s + v.shareCount, 0)

  return (
    <div className="smm-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>TikTok Video Performance</h3>
        {tiktokConns.length > 1 && (
          <select value={selectedConn ?? ''} onChange={e => setSelectedConn(Number(e.target.value))} className="smm-select" style={{ minWidth: 180 }}>
            {tiktokConns.map(c => <option key={c.connectionID} value={c.connectionID}>{c.displayName}</option>)}
          </select>
        )}
        <button onClick={fetchVideos} disabled={loading} className="smm-btn smm-btn-primary smm-btn-sm">
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        {videos.length > 0 && <span className="smm-hint">{videos.length} videos</span>}
      </div>

      {error && <p style={{ color: '#d32f2f', fontSize: '0.85rem' }}>{error}</p>}

      {videos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ textAlign: 'center', padding: '0.75rem', background: '#f0f7ff', borderRadius: 8 }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{totalViews.toLocaleString()}</div>
            <div className="smm-hint">Total Views</div>
          </div>
          <div style={{ textAlign: 'center', padding: '0.75rem', background: '#fef0f0', borderRadius: 8 }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{totalLikes.toLocaleString()}</div>
            <div className="smm-hint">Total Likes</div>
          </div>
          <div style={{ textAlign: 'center', padding: '0.75rem', background: '#f0fff4', borderRadius: 8 }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{totalComments.toLocaleString()}</div>
            <div className="smm-hint">Total Comments</div>
          </div>
          <div style={{ textAlign: 'center', padding: '0.75rem', background: '#fffbf0', borderRadius: 8 }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{totalShares.toLocaleString()}</div>
            <div className="smm-hint">Total Shares</div>
          </div>
        </div>
      )}

      {!loading && videos.length === 0 && !error && (
        <p className="smm-empty">No TikTok videos found. Publish some videos first.</p>
      )}

      {videos.length > 0 && (
        <table className="smm-table">
          <thead>
            <tr>
              <th></th>
              <th>Title</th>
              <th>Views</th>
              <th>Likes</th>
              <th>Comments</th>
              <th>Shares</th>
              <th>Published</th>
            </tr>
          </thead>
          <tbody>
            {videos.map(v => (
              <tr key={v.id}>
                <td style={{ width: 48 }}>
                  {v.thumbnailUrl ? (
                    <img src={v.thumbnailUrl} alt="" style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: 4, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#999' }}>
                      No img
                    </div>
                  )}
                </td>
                <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {v.title || '(untitled)'}
                </td>
                <td>{v.viewCount.toLocaleString()}</td>
                <td>{v.likeCount.toLocaleString()}</td>
                <td>{v.commentCount.toLocaleString()}</td>
                <td>{v.shareCount.toLocaleString()}</td>
                <td>{v.createTime ? new Date(v.createTime * 1000).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

interface EngagementRow {
  engagementId: number
  postTargetId: number
  platformName: string
  postContent: string | null
  likes: number
  comments: number
  shares: number
  reach: number
  impressions: number
  saves: number
  publishedAt: string | null
  fetchedAt: string
}

function PostEngagementGrid({ wsId }: { wsId: number }) {
  const [items, setItems] = useState<EngagementRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [platformFilter, setPlatformFilter] = useState<string>('')
  const [orderBy, setOrderBy] = useState('PublishedAt')
  const [refreshing, setRefreshing] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize), orderBy })
      if (platformFilter) qs.set('platform', platformFilter)
      const res = await api.get<{ totalCount: number; items: EngagementRow[] }>(
        `/workspaces/${wsId}/analytics/post-engagement-v2?${qs}`)
      setItems(res.items)
      setTotalCount(res.totalCount)
    } catch (err) {
      console.error('[SMM] engagement grid fetch failed', err)
    }
  }, [wsId, page, pageSize, platformFilter, orderBy])

  useEffect(() => { fetchData() }, [fetchData])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await api.post(`/workspaces/${wsId}/analytics/refresh-engagement`, {})
      await fetchData()
    } catch (err) {
      console.error('[SMM] refresh engagement failed', err)
    }
    setRefreshing(false)
  }

  const totalPages = Math.ceil(totalCount / pageSize)

  return (
    <div className="smm-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Post Engagement</h3>
        <select value={platformFilter} onChange={e => { setPlatformFilter(e.target.value); setPage(1) }} className="smm-select" style={{ minWidth: 120 }}>
          <option value="">All Platforms</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
          <option value="Threads">Threads</option>
          <option value="TikTok">TikTok</option>
        </select>
        <select value={orderBy} onChange={e => { setOrderBy(e.target.value); setPage(1) }} className="smm-select" style={{ minWidth: 120 }}>
          <option value="PublishedAt">Published Date</option>
          <option value="Likes">Likes</option>
          <option value="Reach">Reach</option>
        </select>
        <button onClick={handleRefresh} disabled={refreshing} className="smm-btn smm-btn-primary smm-btn-sm">
          {refreshing ? 'Refreshing...' : 'Refresh Engagement'}
        </button>
        <span className="smm-hint">{totalCount} posts</span>
      </div>
      {items.length === 0 ? (
        <p className="smm-empty">No engagement data yet. Click "Refresh Engagement" to pull metrics from your platforms.</p>
      ) : (
        <>
          <table className="smm-table">
            <thead>
              <tr><th>Platform</th><th>Content</th><th>Likes</th><th>Comments</th><th>Shares</th><th>Reach</th><th>Impressions</th><th>Saves</th><th>Published</th><th>Fetched</th></tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.engagementId}>
                  <td>{r.platformName}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.postContent?.slice(0, 60) || '-'}</td>
                  <td>{r.likes}</td>
                  <td>{r.comments}</td>
                  <td>{r.shares}</td>
                  <td>{r.reach}</td>
                  <td>{r.impressions}</td>
                  <td>{r.saves}</td>
                  <td>{r.publishedAt ? new Date(r.publishedAt).toLocaleDateString() : '-'}</td>
                  <td>{new Date(r.fetchedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '0.75rem' }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="smm-btn smm-btn-sm">Prev</button>
              <span style={{ lineHeight: '2rem' }}>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="smm-btn smm-btn-sm">Next</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
